import clsx from 'clsx';
import type { MouseEvent, ReactElement } from 'react';

import { CoreTagProps } from '../shared/types/tag.types';
import CoreTag from '../tag/tag';

import styles from './tag-zone.module.scss';
import {
  CoreTagMapping,
  FeatureTagZoneProps,
  MultiBuyPriceProps,
  RoundelProps,
  TagModelMap,
  TagType,
} from './tag-zone.types';

export function FeatureTagZone<T extends TagType>({
  direction = 'horizontal',
  type,
  maxTagsToShow = 3,
  model,
  customStyles,
  onLinkClick,
}: FeatureTagZoneProps<T>) {
  const tagZoneClass = clsx(
    customStyles,
    styles['feature-tag-zone'],
    styles[`feature-tag-zone-${direction}`],
    styles[`feature-tag-zone-${type}`]
  );

  const tags = model?.slice(0, maxTagsToShow) || [];

  const handleLinkClick =
    (url?: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      if (!url || !onLinkClick) {
        return;
      }

      event.preventDefault();
      onLinkClick(event);
    };

  const renderPromotionLinkTag = (props: MultiBuyPriceProps, index: number) => {
    const { label, secondaryLabel, url } = props;
    return (
      <div key={index} className={styles['promotion-link-panel']}>
        {url ? (
          <a
            href={url}
            className={styles['promotion-link']}
            onClick={handleLinkClick(url)}
          >
            {label}
          </a>
        ) : (
          <span className={styles['promotion-label']}>{label}</span>
        )}
        <span className={styles['promotion-price']}>{secondaryLabel}</span>
      </div>
    );
  };

  const roundelImage = (RoundelProps: RoundelProps) => {
    const { imageUrl, fallbackImageUrl, alt } = RoundelProps;
    return (
      (imageUrl || fallbackImageUrl) && (
        <img
          src={imageUrl}
          className={styles.roundelImage}
          alt={alt}
          onError={(e) => {
            e.currentTarget.src = fallbackImageUrl || '';
          }}
        />
      )
    );
  };

  const renderRoundelTag = (props: RoundelProps) => {
    const { imageUrl, alt, url, fallbackImageUrl } = props;
    return url ? (
      <a
        href={url ?? ''}
        tabIndex={-1}
        aria-label={alt}
        onClick={handleLinkClick(url)}
      >
        {roundelImage({ imageUrl, alt, fallbackImageUrl })}
      </a>
    ) : (
      roundelImage({ imageUrl, alt, fallbackImageUrl })
    );
  };

  const renderMap = {
    promotion: (tag: CoreTagProps, index: number) => (
      <div
        key={index}
        className={clsx(tag?.label === 'Boosted' && styles['boosted-tag'])}
      >
        {tag.label && (
          <CoreTag
            {...tag}
            icon={{ name: (tag?.icon as string) || undefined }}
            schemeName={(tag as any)?.scheme}
            size={CoreTagMapping[tag?.size as keyof typeof CoreTagMapping]}
          />
        )}
      </div>
    ),
    promotionLink: (tag: MultiBuyPriceProps, index: number) =>
      renderPromotionLinkTag(tag, index),
    roundel: (tag: RoundelProps, index: number) => (
      <div key={index}>{renderRoundelTag(tag)}</div>
    ),
  };

  return (
    <div className={tagZoneClass}>
      {tags.map((tag, index) => {
        if (!type || !(type in renderMap)) {
          return null;
        }

        const renderer = renderMap[type] as (
          tag: TagModelMap[T],
          index: number
        ) => ReactElement;

        return renderer(tag, index);
      })}
    </div>
  );
}
